/*
 * Web stand-ins for the device modules the app imports (location, storage, clipboard, browser,
 * picker, constants, splash, notifications). Each does the browser's equivalent, or nothing where
 * the web has no such thing (push notifications on a phone's lock screen, for example).
 */

const ls = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

// ---- storage (AsyncStorage and SecureStore) --------------------------------------------------------
const memory = new Map();
const store = {
  async getItem(key) {
    const s = ls();
    return s ? s.getItem(key) : memory.get(key) ?? null;
  },
  async setItem(key, value) {
    const s = ls();
    if (s) s.setItem(key, String(value));
    else memory.set(key, String(value));
  },
  async removeItem(key) {
    const s = ls();
    if (s) s.removeItem(key);
    else memory.delete(key);
  },
  async multiGet(keys) {
    return Promise.all(keys.map(async (k) => [k, await store.getItem(k)]));
  },
  async multiSet(pairs) {
    await Promise.all(pairs.map(([k, v]) => store.setItem(k, v)));
  },
  async multiRemove(keys) {
    await Promise.all(keys.map((k) => store.removeItem(k)));
  },
  async getAllKeys() {
    const s = ls();
    return s ? Object.keys(s) : [...memory.keys()];
  },
  async clear() {
    const s = ls();
    if (s) s.clear();
    else memory.clear();
  },
};
export const AsyncStorage = store;
export const SecureStore = {
  getItemAsync: store.getItem,
  setItemAsync: store.setItem,
  deleteItemAsync: store.removeItem,
};

// ---- location ----------------------------------------------------------------------------------
async function permissionState() {
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch {
    return 'prompt';
  }
}
const asPermission = (state) => ({ status: state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'undetermined', granted: state === 'granted', canAskAgain: state !== 'denied', expires: 'never' });

/**
 * Browsers want a location prompt to follow a tap or click, and Lighthouse flags one that appears on page load.
 * The app asks for permission as soon as a screen opens, so on the web the question is only put to the browser
 * while the visitor's last tap is fresh; otherwise the answer is the current state ("undetermined"), and the
 * screen offers its "Enable location" button, whose tap does ask. Browsers without the activation flag keep
 * the old behaviour.
 */
function userJustActed(): boolean {
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
  return activation ? activation.isActive : true;
}

export const Location = {
  Accuracy: { Lowest: 1, Low: 2, Balanced: 3, High: 4, Highest: 5, BestForNavigation: 6 },
  async getForegroundPermissionsAsync() {
    return asPermission(await permissionState());
  },
  async requestForegroundPermissionsAsync() {
    const state = await permissionState();
    if (state !== 'granted' && !userJustActed()) return asPermission(state);
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(asPermission('denied'));
      navigator.geolocation.getCurrentPosition(
        () => resolve(asPermission('granted')),
        () => resolve(asPermission('denied')),
        { timeout: 15000, maximumAge: 600000 },
      );
    });
  },
  async hasServicesEnabledAsync() {
    return Boolean(navigator.geolocation);
  },
  async enableNetworkProviderAsync() {},
  async getCurrentPositionAsync(options: { accuracy?: number } = {}) {
    if ((await permissionState()) !== 'granted' && !userJustActed()) throw new Error('Location needs permission first.');
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('Location is not available in this browser.'));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ coords: { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy, altitude: pos.coords.altitude, heading: pos.coords.heading, speed: pos.coords.speed, altitudeAccuracy: pos.coords.altitudeAccuracy }, timestamp: pos.timestamp }),
        (err) => reject(new Error(err.message || 'Location is not available.')),
        { enableHighAccuracy: (options.accuracy || 3) >= 4, timeout: 15000, maximumAge: 300000 },
      );
    });
  },
  async reverseGeocodeAsync({ latitude, longitude }) {
    const token = process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN;
    if (!token) return [];
    try {
      const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${token}&limit=1`);
      const data = await res.json();
      const f = data?.features?.[0];
      if (!f) return [];
      const ctx = Object.fromEntries((f.context || []).map((c) => [String(c.id).split('.')[0], c.text]));
      return [{ name: f.text, street: f.text, district: ctx.neighborhood || ctx.locality || null, subregion: ctx.place || null, city: ctx.place || null, region: ctx.region || null, country: ctx.country || null, formattedAddress: f.place_name }];
    } catch {
      return [];
    }
  },
};

// ---- clipboard, browser, picker -----------------------------------------------------------------------
export const Clipboard = {
  async setStringAsync(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },
  async getStringAsync() {
    try {
      return await navigator.clipboard.readText();
    } catch {
      return '';
    }
  },
};

export const WebBrowser = {
  async openBrowserAsync(url) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return { type: 'opened' };
  },
  maybeCompleteAuthSession() {
    return { type: 'success' };
  },
};

export const ImagePicker = {
  MediaTypeOptions: { Images: 'Images', All: 'All', Videos: 'Videos' },
  async getMediaLibraryPermissionsAsync() {
    return { status: 'granted', granted: true, canAskAgain: true };
  },
  async requestMediaLibraryPermissionsAsync() {
    return { status: 'granted', granted: true, canAskAgain: true };
  },
  launchImageLibraryAsync() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/webp';
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) return resolve({ canceled: true, assets: null });
        const uri = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => resolve({ canceled: false, assets: [{ uri, width: img.width, height: img.height, fileName: file.name, mimeType: file.type, fileSize: file.size, file }] });
        img.onerror = () => resolve({ canceled: false, assets: [{ uri, fileName: file.name, mimeType: file.type, fileSize: file.size, file }] });
        img.src = uri;
      };
      input.oncancel = () => resolve({ canceled: true, assets: null });
      input.click();
    });
  },
};

// ---- constants, splash, notifications -----------------------------------------------------------------
export const Constants = { expoConfig: { version: process.env.NEXT_PUBLIC_APP_VERSION ?? '' }, easConfig: {}, manifest: {}, platform: { web: {} }, appOwnership: null, executionEnvironment: 'standalone' };

export const SplashScreen = {
  async preventAutoHideAsync() {},
  async hideAsync() {},
};

const noListener = { remove() {} };
export const Notifications = {
  AndroidImportance: { DEFAULT: 3, HIGH: 4, MAX: 5, LOW: 2, MIN: 1, NONE: 0 },
  setNotificationHandler() {},
  async setNotificationChannelAsync() {},
  async getPermissionsAsync() {
    return { status: 'denied', granted: false, canAskAgain: false };
  },
  async requestPermissionsAsync() {
    return { status: 'denied', granted: false, canAskAgain: false };
  },
  async getExpoPushTokenAsync() {
    throw new Error('Push notifications are not available on the web.');
  },
  addNotificationResponseReceivedListener() {
    return noListener;
  },
  addNotificationReceivedListener() {
    return noListener;
  },
  async getLastNotificationResponseAsync() {
    return null;
  },
};

export function requireOptionalNativeModule() {
  return null;
}
